import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { select } from 'd3';
import * as d3 from 'd3';
import './style.css';

const D3HeatMap = (props) => {
  const alphabet = props?.data;
  const chartRef = useRef(null);
  const ref = useRef(null);

  const [svgWidth, getSvgWidth] = useState();
  const [screenSize, getDimension] = useState({
    dynamicWidth: window.innerWidth
  });

  const setDimension = () => {
    getDimension({
      dynamicWidth: window.innerWidth
    });
  };

  useEffect(() => {
    window.addEventListener('resize', setDimension);

    return () => {
      window.removeEventListener('resize', setDimension);
    };
  }, [screenSize]);

  useEffect(() => {
    setTimeout(() => {
      getSvgWidth(ref?.current?.offsetWidth);
    }, 100);
  }, [screenSize]);

  useEffect(() => {
    d3.select(chartRef.current).select('svg').remove();
    d3.select(chartRef.current).select('div.custom-d3tooltip').remove();

    const graphData = alphabet && alphabet[0]?.values;
    const yAxisData = graphData?.map((d) => d.y);
    const xAxisData = graphData?.map((d) => d.x);

    const longestXaxisString = xAxisData?.sort(function (a, b) {
      return b.length - a.length;
    })[0];

    // Set up the dimensions of the heatmap
    const margin = {
      top: 50,
      right: 10,
      bottom: longestXaxisString?.length * 6.5,
      left: 60
    };
    const container = d3.select(ref.current);
    const width =
      (container.node().getBoundingClientRect().width || svgWidth) -
      margin.left -
      margin.right;
    const height = 300 - margin.top - margin.bottom;

    const xScale = d3.scaleBand().domain(xAxisData).range([0, width]).padding(0.03);
    const yScale = d3.scaleBand().domain(yAxisData).range([height, 0]).padding(0.03);

    // Custom Colors
    const customColors = [ '#176365', '#00FF55', '#FFC300' ];

    const colorScale = d3
      .scaleLinear()
      .domain([0, d3.max(graphData, (d) => d.v)])
      .range(customColors);

    // Create the SVG container for the heatmap
    const svg = select(chartRef.current)
      .append('svg')
      .attr('class', 'custom-heatmap-svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const xAxis = d3.axisBottom(xScale);
    const yAxis = d3.axisLeft(yScale);

    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis)
      .call((g) =>
        g
          .selectAll('.tick Text')
          .style('transform', 'rotate(90deg)')
          .attr('text-anchor', 'start')
          .style('font-size', '11px')
          .attr('dy', '-.5em')
          .attr('dx', '.6em')
      );

    svg.append('g').attr('class', 'y-axis').call(yAxis);

    // Tooltip
    const tooltip = d3
      .select('body')
      .append('div.custom-d3tooltip')
      .style('position', 'absolute')
      .style('text-align', 'center')
      .style('padding', '2px')
      .style('font', '12px sans-serif')
      .style('background', 'lightgrey')
      .style('border', '0px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    // Append heatmap rectangles
    svg
      .append('g')
      .selectAll('.heatmap-rect')
      .data(graphData)
      .enter()
      .append('rect')
      .attr('class', 'heatmap-rect')
      .attr('x', (d) => xScale(d.x))
      .attr('y', (d) => yScale(d.y))
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .attr('fill', (d) => colorScale(d.v))
      .on('mouseover', function (d, i, e) {
        d3.select(this).attr('opacity', '.5');
        tooltip.transition().duration(200).style('opacity', 0.9);
        tooltip
          .html(
            `<div class="custom-chart-tooltip">
                <table class="content">
                <tbody>
                <tr class="line">
                <td colspan="1" class="text-start">${i.x}</td>
                <td colspan="1" class="right-align">Count</td>
                </tr>
                <tr>
                <td colspan="1" class="text-start">${i.y}</td>
                <td colspan="1" class="right-align bold">${i.v}</td>
                </tr>
                </tbody>
                </table>
                </div>`
          )
          .style('left', d.pageX + 10 + 'px')
          .style('top', d.pageY + 10 + 'px');
      })
      .on('mouseout', function (d, i, e) {
        tooltip.transition().duration(500).style('opacity', 0);
        d3.select(this).attr('opacity', '1');
      })
      .text((d) => d.value);

    // Create Legend
    svg
      .selectAll('.legendRect')
      .data(customColors)
      .join('rect')
      .attr('class', 'legendRect')
      .attr('x', (d, i) => i * 60)
      .attr('y', -40)
      .attr('width', 60)
      .attr('height', 6)
      .attr('fill', (d) => d);

    const formatValue = d3.format('.2s');

    svg
      .selectAll('.legendText')
      .data(customColors)
      .join('text')
      .attr('class', 'legendText')
      .style('font-size', '10px')
      .attr('x', (d, i) => i * 60)
      .attr('y', -22)
      .text((d, i) => {
        const rangeStart = Math.round(
          i *
            d3.max(graphData, (d) => d3.max(graphData, (d) => d.v) / customColors.length)
        );
        const rangeEnd = Math.round(
          (i + 1) *
            d3.max(graphData, (d) => d3.max(graphData, (d) => d.v) / customColors.length)
        );
        return `${formatValue(rangeStart)}-${formatValue(rangeEnd)}`;
      })
      .attr('text-anchor', 'center');
  }, [alphabet, svgWidth]);

  return (
    <>
      <div ref={ref} className="custom-heatmap- w-100">
        <div className="w-100" ref={chartRef}></div>
      </div>
    </>
  );
};

export default D3HeatMap;
